import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Icon } from "components"
import { useAppDispatch, useAppSelector, useFeatureFlags } from "hooks"
import { getUserRoles, saveRole, selectRoleList, selectUserAccessModalLoading } from "store/Security/roles.slice"
import { Switch } from "antd"
import { USER_ACCESS_MAPPING } from "config/constants"
import { selectCurrentUser, selectLoggedInUserRoles } from "store/auth.slice"
import { GetRoleInterface } from "@type/security.types"
import { FeatureFlagEnums } from "types/featureFlags.type"
import AntdModal from "./AntdModal"
import { Collapse, Select } from "antd-v5"
import { Controller, ControllerRenderProps, FieldValues, useForm } from "react-hook-form"
import { getBranches, selectBranchListSorted } from "store/Branches/branch.slice"
import { getCustomers, selectSortedCustomers } from "store/Customers/customers.slice"
import { getLocations, selectSortedActiveSiteList } from "store/Location/locationDetails.slice"
import { getSDSuppliers, selectSortedSupplierList } from "store/StaticData/SDSuppliers.slice"
import { getEmployees, selectSortedEmployeeList } from "store/Employee/detail.slice"

const { Panel } = Collapse

interface Props {
	open: boolean
	modalHandler: (open: boolean) => void
	selectedUser: string | null
}

const superAdminAllowedFunctions = {
	PayRoll: {
		VIEW_PAYROLL: "View",
		FEATURE_NAME: FeatureFlagEnums.ACCOUNTS,
	},
	"SIA Validation": {
		BYPASS_SIA_VALIDATION: "Allow Bypass",
		FEATURE_NAME: FeatureFlagEnums.ADMIN,
	},
	"NIN Validation": {
		BYPASS_NIN_VALIDATION: "Allow Bypass",
		FEATURE_NAME: FeatureFlagEnums.ADMIN,
	},
	"Max Consecutive Workdays Validation": {
		BYPASS_MAX_CONSECUTIVE_WORKDAYS_VALIDATION: "Allow Bypass",
		FEATURE_NAME: FeatureFlagEnums.ADMIN,
	},
	"Site Manager": {
		SITE_MANAGER: "Allow Bypass",
		FEATURE_NAME: FeatureFlagEnums.ADMIN,
	},
}

const allowedFunctionsEnum = {
	Employee: {
		CREATE_AND_UPDATE_EMPLOYEE: "Create/Update",
		VIEW_EMPLOYEE: "View",
		FEATURE_NAME: FeatureFlagEnums.EMPLOYEES,
	},
	Location: {
		CREATE_AND_UPDATE_LOCATION: "Create/Update",
		VIEW_LOCATION: "View",
		FEATURE_NAME: FeatureFlagEnums.LOCATIONS,
	},
	Schedules: {
		CREATE_AND_UPDATE_SCHEDULES: "Create/Update",
		VIEW_SCHEDULES: "View",
		FEATURE_NAME: FeatureFlagEnums.SCHEDULES,
	},
	"Work Monitor": {
		CREATE_AND_UPDATE_WORK_MONITOR: "Create/Update",
		VIEW_WORK_MONITOR: "View",
		FEATURE_NAME: FeatureFlagEnums.COMMAND_CENTER,
	},
	Admin: {
		VIEW_ADMIN: "View",
		FEATURE_NAME: FeatureFlagEnums.ADMIN,
	},
	// Dashboard: {
	// 	VIEW_DASHBOARD: "View",
	// 	FEATURE_NAME: FeatureFlagEnums.DASHBOARD,
	// },
	Reports: {
		VIEW_REPORTS: "Allowed",
		DOWNLOAD_REPORTS: "Print/Download",
		FEATURE_NAME: FeatureFlagEnums.REPORTS,
	},
}

type listNames =
	| "CREATE_AND_UPDATE_EMPLOYEE"
	| "VIEW_EMPLOYEE"
	| "CREATE_AND_UPDATE_LOCATION"
	| "VIEW_LOCATION"
	| "CREATE_AND_UPDATE_SCHEDULES"
	| "VIEW_SCHEDULES"
	| "CREATE_AND_UPDATE_WORK_MONITOR"
	| "VIEW_WORK_MONITOR"
	| "VIEW_ADMIN"
	| "VIEW_DASHBOARD"
	| "VIEW_REPORTS"
	| "DOWNLOAD_REPORTS"
	| "VIEW_PAYROLL"

const UserAccessModal: React.FC<Props> = ({ open, modalHandler, selectedUser }) => {
	const dispatch = useAppDispatch()
	const { featureFlagEnabled } = useFeatureFlags()

	const currentUser = useAppSelector(selectCurrentUser())
	const isUserAccessModalLoading = useAppSelector(selectUserAccessModalLoading())
	const selectBranchList = useAppSelector(selectBranchListSorted())
	const selectCustomerList = useAppSelector(selectSortedCustomers)
	const selectSiteList = useAppSelector(selectSortedActiveSiteList())
	const selectSupplierListOptions = useAppSelector(selectSortedSupplierList)
	const selectEmployeeList = useAppSelector(selectSortedEmployeeList())
	const selectedUserRoleList = useAppSelector(selectRoleList)
	const loggedInUserRoles = useAppSelector(selectLoggedInUserRoles)
	const [allowedFunctions, setAllowedFunctions] = useState<string[]>([])
	const [showDropDown, setShowDropDown] = useState<boolean>(false)
	const [hasCompanyWideAccess, setHasCompanyWideAccess] = useState(false)
	const currentLoggedInUser = useAppSelector(selectCurrentUser())
	const [activePanels, setActivePanels] = useState(loggedInUserRoles.map(c => c.companyName))

	const { control, handleSubmit, watch, setValue, reset } = useForm()

	type ResourceKeyFiltersValue = Record<string, Partial<{ branch: string; customer: string; supplier: string }>>
	type ResourceKeyFilters = Record<string, ResourceKeyFiltersValue>

	const resourceKeyToListFilter: ResourceKeyFilters = useMemo(() => {
		return {
			employee: selectEmployeeList.reduce((acc, cur) => {
				acc[cur._id] = {
					branch: cur?.branch ?? "",
					supplier: cur?.supplier ?? "",
				}
				return acc
			}, {} as ResourceKeyFiltersValue),

			site: selectSiteList.reduce((acc, cur) => {
				acc[cur._id] = {
					branch: cur?.branch?._id ?? "",
					customer: cur?.customer?._id ?? "",
				}
				return acc
			}, {} as ResourceKeyFiltersValue),
		}
	}, [selectEmployeeList, selectSiteList])

	useEffect(() => {
		dispatch(getEmployees())
		dispatch(getLocations())
		dispatch(getBranches(true))
		dispatch(getCustomers(true, () => undefined))
		dispatch(getSDSuppliers(true))
	}, [])

	const companies = useMemo(() => {
		return loggedInUserRoles.map(role => {
			return {
				_id: role.companyId,
				name: role.companyName,
				allowedResources: {
					branch: selectBranchList
						.filter(({ company }) => company === role.companyId)
						.map(branch => ({ label: branch.name, value: branch._id })),
					customer: selectCustomerList
						.filter(({ company }) => company === role.companyId)
						.map(customer => ({ label: customer.name, value: customer._id })),
					site: selectSiteList
						.filter(({ company = "" }) => company === role.companyId)
						.map(site => ({ label: site.name, value: site._id })),
					supplier: selectSupplierListOptions
						.filter(({ company }) => company === role.companyId)
						.map(supplier => ({ label: supplier.name, value: supplier._id })),
					employee: selectEmployeeList
						.filter(({ company = "" }) => company === role.companyId)
						.map(employee => ({ label: employee.shortName, value: employee._id })),
				},
			}
		})
	}, [
		loggedInUserRoles,
		selectBranchList,
		selectCustomerList,
		selectSiteList,
		selectSupplierListOptions,
		selectEmployeeList,
	])

	const handleDropDown = () => {
		setShowDropDown(prev => !prev)
	}

	useEffect(() => {
		if (selectedUser) dispatch(getUserRoles(selectedUser))
	}, [selectedUser])

	useEffect(() => {
		resetForm()
	}, [selectedUserRoleList, selectedUserRoleList.length])

	const resetForm = () => {
		if (selectedUserRoleList?.length) {
			const initialPermissions = createInitialPermissions(selectedUserRoleList)

			reset(initialPermissions)

			// This is the old code - still needs to be checked.
			const functionsPermitted = Object.entries(USER_ACCESS_MAPPING).filter(entry => {
				return selectedUserRoleList[0].functionsPermitted?.some(func => {
					return entry[1]?.includes(func)
				})
			})
			setAllowedFunctions(Object.values(functionsPermitted).map(val => val[0]))
		} else {
			setAllowedFunctions([])
			reset({})
		}

		const doesUserHasCompanyWideAccess = selectedUserRoleList.some(
			({ hasCompanyWideAccess }) => !!hasCompanyWideAccess,
		)

		setHasCompanyWideAccess(doesUserHasCompanyWideAccess)
	}

	const createInitialPermissions = useCallback((list: GetRoleInterface[]) => {
		if (!list?.length) return {}

		return list.reduce((acc: Record<string, any>, role) => {
			const {
				_id = "",
				companyId = "",
				isOrganizationalAdmin,
				isAllBranchesAllowed,
				isAllCustomersAllowed,
				isAllSitesAllowed,
				isAllSuppliersAllowed,
				isAllEmployeesAllowed,
				allowedResources = {},
			} = role

			acc[companyId] = {
				isOrganizationalAdmin,
				role: _id,
				allowedResources: {
					branch: {
						all: isAllBranchesAllowed,
						selected: isAllBranchesAllowed ? [] : allowedResources?.branch ?? [],
					},
					customer: {
						all: isAllCustomersAllowed,
						selected: isAllCustomersAllowed ? [] : allowedResources?.customer ?? [],
					},
					site: {
						all: isAllSitesAllowed,
						selected: isAllSitesAllowed ? [] : allowedResources?.site ?? [],
					},
					supplier: {
						all: isAllSuppliersAllowed,
						selected: isAllSuppliersAllowed ? [] : allowedResources?.supplier ?? [],
					},
					employee: {
						all: isAllEmployeesAllowed,
						selected: isAllEmployeesAllowed ? [] : allowedResources?.employee ?? [],
					},
				},
			}

			return acc
		}, {})
	}, [])

	const onSubmit = (data: any) => {
		const functionsPermitted: string[] = []

		allowedFunctions.forEach(func => {
			functionsPermitted.push(...USER_ACCESS_MAPPING[func as listNames])
		})

		const payload = {
			payload: data,
			hasCompanyWideAccess,
			functionsPermitted,
		}

		dispatch(saveRole(payload, selectedUser ?? "", () => modalHandler(false)))
	}

	const handleAccessChange = (
		values: string[],
		field: ControllerRenderProps<FieldValues, string>,
		resourceKey: string,
		company: typeof companies[0],
	) => {
		const branchKey = `${company._id}.allowedResources.branch.selected`
		const supplierKey = `${company._id}.allowedResources.supplier.selected`
		const customerKey = `${company._id}.allowedResources.customer.selected`

		const branchToSelect = new Set<string>()
		const supplierToSelect = new Set<string>()
		const customerToSelect = new Set<string>()

		values.forEach(value => {
			const valueToSelect = resourceKeyToListFilter[resourceKey]?.[value] || {}

			if (resourceKey === "employee") {
				if (valueToSelect.supplier) supplierToSelect.add(valueToSelect.supplier)
			} else if (resourceKey === "site") {
				if (valueToSelect.customer) customerToSelect.add(valueToSelect.customer)
			}
			if (valueToSelect.branch) branchToSelect.add(valueToSelect.branch)
		})

		const uniqueBranchesSelected = [...new Set([...branchToSelect, ...(watch(branchKey) ?? [])])]
		const uniqueSuppliersSelected = [...new Set([...supplierToSelect, ...(watch(supplierKey) ?? [])])]
		const uniqueCustomersSelected = [...new Set([...customerToSelect, ...(watch(customerKey) ?? [])])]

		setValue(branchKey, uniqueBranchesSelected)
		setValue(supplierKey, uniqueSuppliersSelected)
		setValue(customerKey, uniqueCustomersSelected)

		field.onChange(values)
	}

	return (
		<AntdModal
			open={open}
			width={1500}
			destroyOnClose
			onCancel={() => {
				resetForm()
				modalHandler(false)
			}}
			onOk={() => handleSubmit(onSubmit)()}
			okText="Save"
			title="User Access"
		>
			{isUserAccessModalLoading ? (
				<div className="flex h-full w-[1100px] items-center justify-center py-10">
					<Icon name="spinner" color="white" size={80} className="mb-16 animate-spin !text-purple" />
				</div>
			) : (
				<>
					<div className="space-y-15 mb-4 min-w-[1045px] flex-col items-center justify-center">
						<div className="flex w-full space-x-6">
							<div className="w-2/3">
								<form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
									{currentLoggedInUser?.roles.some(
										({ hasCompanyWideAccess }) => hasCompanyWideAccess,
									) && (
										<div className="mb-4 flex items-center gap-2">
											<span className="font-semibold">Full Access (All Companies):</span>
											<Switch
												checked={hasCompanyWideAccess}
												onChange={checked => {
													setHasCompanyWideAccess(checked)
													setActivePanels(checked ? [] : companies.map(c => c.name))
												}}
											/>
										</div>
									)}

									<Collapse
										activeKey={hasCompanyWideAccess ? [] : activePanels}
										onChange={keys => setActivePanels(keys as string[])}
									>
										{companies.map(company => {
											return (
												<Panel
													collapsible={hasCompanyWideAccess ? "disabled" : undefined}
													header={company.name}
													key={company.name}
												>
													<div>
														<Controller
															control={control}
															name={`${company._id}.isOrganizationalAdmin`}
															render={({ field: { value, onChange } }) => (
																<label className="mb-4 flex items-center gap-2">
																	<span>Full Admin Access:</span>
																	<Switch checked={value} onChange={onChange} />
																</label>
															)}
														/>

														<div className="my-4 border-t border-gray-200" />

														{Object.entries(company.allowedResources).map(
															([resourceKey, resourceValue]) => {
																const fullAccessPath = `${company._id}.allowedResources.${resourceKey}.all`
																const selectedPath = `${company._id}.allowedResources.${resourceKey}.selected`
																const fullAccessValue = watch(fullAccessPath)

																if (hasCompanyWideAccess) setValue(selectedPath, [])

																return (
																	<div key={resourceKey} className="mb-6">
																		<label className="mb-2 flex items-center gap-2 text-gray-700">
																			<Controller
																				control={control}
																				name={fullAccessPath}
																				render={({ field }) => (
																					<>
																						<Switch
																							{...field}
																							checked={field.value}
																						/>
																						<span>
																							Give Access to All{" "}
																							{resourceKey}
																						</span>
																					</>
																				)}
																			/>
																		</label>
																		<Controller
																			control={control}
																			name={selectedPath}
																			render={({ field }) => (
																				<Select
																					mode="multiple"
																					{...field}
																					onChange={(values: string[]) => {
																						handleAccessChange(
																							values,
																							field,
																							resourceKey,
																							company,
																						)
																					}}
																					style={{ width: "100%" }}
																					filterOption={(input, option) =>
																						(option?.label ?? "")
																							.toLowerCase()
																							.includes(
																								input.toLowerCase(),
																							)
																					}
																					placeholder={`Select ${resourceKey}`}
																					disabled={fullAccessValue}
																					options={resourceValue}
																				/>
																			)}
																		/>
																	</div>
																)
															},
														)}
													</div>
												</Panel>
											)
										})}
									</Collapse>
								</form>
							</div>
							<div className="w-1/3 ">
								<div className="rounded-lg border-[0.75px] p-2 font-semibold">
									<div className="mb-4  flex justify-between">
										<div
											className="flex h-12 w-full cursor-pointer items-center"
											onClick={handleDropDown}
										>
											<Icon
												name={showDropDown ? "dropdownArrow" : "dropRightArrow"}
												size={20}
												color="black"
											/>
											<span>Allowed Functions</span>
										</div>
									</div>

									<div
										className={`flex flex-col space-y-2 p-2 ${
											showDropDown ? "visible" : " hidden"
										} `}
									>
										<div className="mb-4 flex-col space-y-8">
											{Object.entries({
												...allowedFunctionsEnum,
												...(currentUser?.isSuperAdmin && superAdminAllowedFunctions),
											})
												.filter(([_, value]) => featureFlagEnabled(value?.FEATURE_NAME))
												.map(([key, { ...value }], index) => {
													delete (value as any).FEATURE_NAME
													return (
														<div className="flex-col space-y-2" key={index}>
															<span>{key}</span>
															<div className="flex justify-between ">
																{Object.entries(value).map(([cKey, cValue], cIndex) => {
																	return (
																		<div
																			className="flex space-x-2 text-md font-normal"
																			key={cIndex}
																		>
																			<span className="">{cValue}</span>
																			<Switch
																				checked={allowedFunctions.includes(
																					cKey,
																				)}
																				onChange={() => {
																					setAllowedFunctions([
																						...(allowedFunctions.includes(
																							cKey,
																						)
																							? allowedFunctions.filter(
																									func =>
																										func !== cKey,
																							  )
																							: [
																									...allowedFunctions,
																									cKey,
																							  ]),
																					])
																				}}
																			></Switch>
																		</div>
																	)
																})}
															</div>
														</div>
													)
												})}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			)}
		</AntdModal>
	)
}
export default UserAccessModal
